import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isUserAuthenticated } from 'common/selectors/auth';
import { getCurrentUser } from 'aws-amplify/auth';
import ContentShareLayout from './ContentShareLayout';
import LoadingSpinner from '../../../common/components/LoadingSpinner';

/**
 * Redirect component for /content route that redirects authenticated users
 * to /app/content to get the full portal experience with sidebar.
 * Guest users are shown the ContentShareLayout directly.
 */
const ContentShareRedirect = () => {
  const reduxAuthenticated = useSelector(isUserAuthenticated);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [amplifyAuthenticated, setAmplifyAuthenticated] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  // Check both Amplify and Redux auth state
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await getCurrentUser();
        setAmplifyAuthenticated(true);
      } catch {
        setAmplifyAuthenticated(false);
      } finally {
        setIsChecking(false);
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    // Only redirect once we've checked auth state
    if (isChecking || amplifyAuthenticated === null) return;

    // If user is authenticated (either via Amplify or Redux), redirect to /app/content
    if (amplifyAuthenticated || reduxAuthenticated) {
      const queryString = searchParams.toString();
      navigate(`/app/content${queryString ? `?${queryString}` : ''}`, { replace: true });
    }
  }, [isChecking, amplifyAuthenticated, reduxAuthenticated, navigate, searchParams]);

  // Show loading while checking authentication
  if (isChecking) {
    return <LoadingSpinner />;
  }

  // For authenticated users, show nothing while redirecting
  if (amplifyAuthenticated || reduxAuthenticated) {
    return null;
  }

  // For guest users, render the layout directly
  return <ContentShareLayout />;
};

export default ContentShareRedirect;

